import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Inbox, RefreshCw, Sparkles, ChevronLeft,
  ArrowUpRight, ExternalLink, ChevronDown, Download,
  FileText, Send, Smile, Mic, Plus,
} from "lucide-react";
import { Avatar, timeAgo, CRM_STATUS_OPTIONS } from "./InboxPage.helpers";

/* ── Message bubble ─────────────────────────────────────────────────────────── */
function MessageItem({ msg, contactName }) {
  const sent = msg.direction === "sent";
  return (
    <div data-testid={`message-${msg.id}`} className={`flex items-end gap-2 sm:gap-3 ${sent ? "flex-row-reverse ml-auto max-w-[85%] sm:max-w-[70%]" : "max-w-[85%] sm:max-w-[70%]"}`}>
      {!sent && (
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex-shrink-0 mb-1 flex items-center justify-center text-xs font-bold text-white" style={{ background: "#262626" }}>
          {(contactName || "?")[0].toUpperCase()}
        </div>
      )}
      <div className="space-y-1 min-w-0 flex-1">
        <div
          className={`p-3 sm:p-4 rounded-2xl text-sm leading-relaxed shadow-sm break-words ${
            sent ? "rounded-br-none" : "rounded-bl-none"
          }`}
          style={sent
            ? { background: "rgba(252,216,63,0.10)", border: "1px solid rgba(252,216,63,0.20)", color: "#fff8e1" }
            : { background: "#20201f", color: "#ffffff" }
          }
        >
          <p className="whitespace-pre-wrap break-words">{msg.body_text || "(Email HTML)"}</p>
        </div>
        <div className={`flex items-center gap-2 px-1 ${sent ? "justify-end" : "justify-start"}`}>
          <span className="text-[10px]" style={{ color: "#6b7280" }}>{timeAgo(msg.created_at)}</span>
          {sent && <span className="text-[10px]" style={{ color: "#fcd83f" }}>✓✓</span>}
        </div>
      </div>
    </div>
  );
}

function MessagesSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      {[...Array(3)].map((_, i) => (
        <div key={`sk-msg-${i}`} className={`flex ${i % 2 === 0 ? "justify-end" : "justify-start"}`}>
          <div className="w-2/3 h-16 rounded-2xl animate-pulse" style={{ background: "rgba(255,255,255,0.05)" }} />
        </div>
      ))}
    </div>
  );
}

/* ── CRM status dropdown ─────────────────────────────────────────────────────── */
function CrmStatusDropdown({ crmContact, showDrop, dropRef, onToggle, onSelect, updating }) {
  const current = CRM_STATUS_OPTIONS.find(o => o.value === crmContact.status);
  return (
    <div className="relative" ref={dropRef}>
      <button
        data-testid="crm-status-dropdown-btn"
        onClick={onToggle}
        disabled={updating}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)", color: current?.color || "#9ca3af" }}
      >
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: current?.color || "#9ca3af" }} />
        {current?.label || "Lead"}
        <ChevronDown size={11} className={`transition-transform ${showDrop ? "rotate-180" : ""}`} />
      </button>
      {showDrop && (
        <div className="absolute right-0 top-full mt-1.5 rounded-xl py-1.5 z-50 shadow-2xl min-w-[140px]" style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.10)" }}>
          {CRM_STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              data-testid={`status-option-${opt.value}`}
              onClick={() => onSelect(opt.value)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold transition-colors hover:bg-white/5"
            >
              <span className="w-2 h-2 rounded-full" style={{ background: opt.color }} />
              <span style={{ color: opt.color }}>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Conversation view (center + right sidebar) ──────────────────────────────── */
export function ThreadView({
  selected, threadData, crmContact, loadingThread, messagesEndRef,
  replyText, setReplyText, replySubject, setReplySubject, onSendReply, sending,
  onAISuggest, suggesting,
  showStatusDrop, statusDropRef, onToggleStatusDrop, onUpdateCrmStatus, updatingStatus,
  onBack,
}) {
  const navigate = useNavigate();
  // Memoized: only the first 2 attachments are shown in the sidebar thumbnail row.
  // Without useMemo the filter+slice runs every render (on every keystroke of the reply box).
  const attachmentThumbs = useMemo(
    () => (threadData?.messages || []).filter((m) => m.has_attachment).slice(0, 2),
    [threadData?.messages]
  );

  if (!selected) {
    return (
      <div className="hidden md:flex flex-col items-center justify-center flex-1 text-center px-8" style={{ background: "#0e0e0e" }}>
        {/* Background glow effects */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-20">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-[120px]" style={{ background: "#ffd16c" }} />
          <div className="absolute top-1/2 left-1/4 w-64 h-64 rounded-full blur-[100px] opacity-30" style={{ background: "#3199BA" }} />
        </div>
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 relative z-10" style={{ background: "rgba(255,255,255,0.05)" }}>
          <Inbox size={28} style={{ color: "rgba(255,255,255,0.2)" }} />
        </div>
        <p className="text-sm font-bold relative z-10" style={{ color: "rgba(255,255,255,0.3)" }}>Seleccioná una conversación</p>
        <p className="text-xs mt-1 relative z-10" style={{ color: "rgba(255,255,255,0.2)" }}>Los emails y respuestas aparecerán aquí</p>
      </div>
    );
  }

  return (
    <>
      {/* ── Center: Conversation ──────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col relative overflow-hidden" style={{ background: "#0e0e0e" }}>
        {/* Background glow spots (low opacity) */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-20 z-0">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-[120px]" style={{ background: "#ffd16c" }} />
          <div className="absolute top-1/2 left-1/4 w-64 h-64 rounded-full blur-[100px] opacity-30" style={{ background: "#3199BA" }} />
        </div>

        {/* Conversation header */}
        <div
          className="flex items-center justify-between gap-2 px-4 sm:px-8 py-3 sm:py-4 flex-shrink-0 z-10"
          style={{ background: "rgba(26,26,26,0.60)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}
        >
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <button className="md:hidden p-2 -ml-2 text-gray-400 hover:text-white flex-shrink-0" onClick={onBack}>
              <ChevronLeft size={20} />
            </button>
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: "#262626" }}>
              {(selected.contact_name || "?")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="font-bold text-white text-sm sm:text-base truncate" style={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>{selected.contact_name}</h2>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: "#fcd83f" }} />
                <span className="text-[10px] font-medium uppercase tracking-wider truncate" style={{ color: "#9ca3af" }}>
                  {selected.contact_email || "Activa ahora"}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0" style={{ color: "#9ca3af" }}>
            {crmContact && (
              <CrmStatusDropdown
                crmContact={crmContact}
                showDrop={showStatusDrop}
                dropRef={statusDropRef}
                onToggle={onToggleStatusDrop}
                onSelect={onUpdateCrmStatus}
                updating={updatingStatus}
              />
            )}
            {crmContact && (
              <button
                data-testid="view-in-crm-btn"
                onClick={() => navigate(`/crm/${crmContact.id}`)}
                className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all"
                style={{ background: "rgba(255,199,7,0.10)", border: "1px solid rgba(255,199,7,0.25)", color: "#ffd16c" }}
              >
                <ExternalLink size={11} /> CRM
              </button>
            )}
            <a href={`mailto:${selected.contact_email}`} className="p-1.5 sm:p-2 rounded-full transition-all hover:bg-white/5">
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 sm:space-y-8 z-10">
          {/* Date separator */}
          <div className="flex justify-center">
            <span
              className="text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1 rounded-full"
              style={{ color: "#6b7280", background: "rgba(255,255,255,0.05)" }}
            >
              Hoy
            </span>
          </div>

          {loadingThread ? (
            <MessagesSkeleton />
          ) : (threadData?.messages || []).length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-center">
              <p className="text-xs" style={{ color: "#6b7280" }}>Sin mensajes en este hilo</p>
            </div>
          ) : (
            (threadData?.messages || []).map(msg => (
              <MessageItem key={msg.id} msg={msg} contactName={selected.contact_name} />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message input */}
        <div
          className="p-3 sm:p-8 z-10"
          style={{ background: "rgba(14,14,14,0.80)", backdropFilter: "blur(12px)" }}
        >
          <div
            className="max-w-4xl mx-auto flex flex-col gap-2 rounded-2xl p-2 pl-3 sm:pl-4 transition-all shadow-2xl"
            style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.05)" }}
          >
            {typeof replySubject === "string" && (
              <input
                data-testid="reply-subject"
                type="text"
                value={replySubject}
                onChange={e => setReplySubject && setReplySubject(e.target.value)}
                placeholder="Asunto"
                className="w-full bg-transparent text-sm text-white placeholder-neutral-500 outline-none px-1 py-1 border-b border-white/5 font-semibold"
              />
            )}
            <div className="flex items-start gap-2 sm:gap-4">
              <button className="hidden sm:block transition-colors flex-shrink-0 mt-2" style={{ color: "#6b7280" }}
                onMouseEnter={e => e.currentTarget.style.color = "#fcd83f"}
                onMouseLeave={e => e.currentTarget.style.color = "#6b7280"}
              >
                <Plus size={20} />
              </button>
            <textarea
              data-testid="reply-textarea"
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Escribe un mensaje..."
              rows={Math.min(12, Math.max(1, (replyText.match(/\n/g) || []).length + 1))}
              onKeyDown={e => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  onSendReply();
                }
              }}
              className="flex-1 min-w-0 bg-transparent text-sm text-white placeholder-neutral-500 outline-none py-3 border-none resize-none leading-relaxed"
              style={{ maxHeight: "40vh" }}
            />
            <div className="flex items-center gap-1 sm:gap-2 px-1 sm:px-2 flex-shrink-0">
              <button
                data-testid="ai-suggest-btn"
                onClick={onAISuggest}
                disabled={suggesting}
                title="Regenerar con IA"
                className="p-1 transition-colors disabled:opacity-50"
                style={{ color: "#6b7280" }}
                onMouseEnter={e => e.currentTarget.style.color = "#fcd83f"}
                onMouseLeave={e => e.currentTarget.style.color = "#6b7280"}
              >
                {suggesting ? <RefreshCw size={18} className="animate-spin" /> : <Sparkles size={18} />}
              </button>
              <button className="hidden sm:block transition-colors" style={{ color: "#6b7280" }}
                onMouseEnter={e => e.currentTarget.style.color = "#fcd83f"}
                onMouseLeave={e => e.currentTarget.style.color = "#6b7280"}
              >
                <Smile size={18} />
              </button>
              <button className="hidden sm:block transition-colors" style={{ color: "#6b7280" }}
                onMouseEnter={e => e.currentTarget.style.color = "#fcd83f"}
                onMouseLeave={e => e.currentTarget.style.color = "#6b7280"}
              >
                <Mic size={18} />
              </button>
              <button
                data-testid="send-reply-btn"
                onClick={onSendReply}
                disabled={sending || !replyText.trim()}
                className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl transition-all disabled:opacity-40 active:scale-95 hover:scale-105"
                style={{ background: "linear-gradient(135deg, #ffd16c, #fdc003)", color: "#604700", boxShadow: "0 4px 14px rgba(255,209,108,0.20)" }}
              >
                {sending ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right sidebar: Contact detail ──────────────────────────────────────── */}
      <aside
        className="hidden xl:flex w-72 flex-col overflow-y-auto flex-shrink-0"
        style={{ borderLeft: "1px solid rgba(255,255,255,0.05)", background: "#131313" }}
      >
        {/* Profile */}
        <div className="p-8 text-center" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
          <div
            className="w-24 h-24 mx-auto rounded-3xl flex items-center justify-center text-3xl font-black text-white mb-4"
            style={{ background: "#262626", border: "2px solid rgba(252,216,63,0.20)", padding: 4 }}
          >
            {(selected.contact_name || "?")[0].toUpperCase()}
          </div>
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {selected.contact_name}
          </h3>
          <p className="text-xs font-medium uppercase tracking-wider mb-6 mt-1" style={{ color: "#6b7280" }}>
            {crmContact?.business_name || "Contacto"}
          </p>

          <div className="flex justify-center gap-3">
            {crmContact && (
              <button
                onClick={() => navigate(`/crm/${crmContact.id}`)}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
                style={{ background: "#262626" }}
                onMouseEnter={e => e.currentTarget.style.color = "#ffd16c"}
                onMouseLeave={e => e.currentTarget.style.color = "#9ca3af"}
              >
                <ExternalLink size={18} style={{ color: "#9ca3af" }} />
              </button>
            )}
            <a
              href={`mailto:${selected.contact_email}`}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
              style={{ background: "#262626" }}
            >
              <ArrowUpRight size={18} style={{ color: "#9ca3af" }} />
            </a>
          </div>
        </div>

        {/* Contact info */}
        <div className="p-6 space-y-8">
          <div>
            <h4 className="text-[10px] font-bold uppercase tracking-widest mb-4" style={{ color: "#6b7280" }}>
              Información de Contacto
            </h4>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-lg" style={{ color: "#484847" }}>@</span>
                <span className="text-xs" style={{ color: "#d1d5db" }}>{selected.contact_email}</span>
              </div>
              {crmContact?.phone && (
                <div className="flex items-center gap-3">
                  <span className="text-sm" style={{ color: "#484847" }}>📱</span>
                  <span className="text-xs" style={{ color: "#d1d5db" }}>{crmContact.phone}</span>
                </div>
              )}
              {crmContact?.city && (
                <div className="flex items-center gap-3">
                  <span className="text-sm" style={{ color: "#484847" }}>📍</span>
                  <span className="text-xs" style={{ color: "#d1d5db" }}>{crmContact.city}</span>
                </div>
              )}
            </div>
          </div>

          {threadData?.messages?.length > 0 && (
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-widest mb-4" style={{ color: "#6b7280" }}>
                Archivos Compartidos
              </h4>
              <div className="grid grid-cols-3 gap-2">
                {attachmentThumbs.map((m) => (
                  <div key={m.id || m._id || m.message_id} className="aspect-square rounded-lg flex items-center justify-center" style={{ background: "#262626", border: "1px solid rgba(255,255,255,0.05)" }}>
                    <FileText size={16} style={{ color: "#ffd16c" }} />
                  </div>
                ))}
                <div
                  className="aspect-square rounded-lg flex items-center justify-center cursor-pointer transition-all"
                  style={{ background: "#262626", border: "1px solid rgba(255,255,255,0.05)", color: "#6b7280" }}
                >
                  <span className="text-[10px] font-bold">+{threadData.messages?.length || 0}</span>
                </div>
              </div>
            </div>
          )}

          {crmContact && (
            <button
              className="w-full py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2"
              style={{
                background: "rgba(255,115,81,0.10)",
                border: "1px solid rgba(255,115,81,0.20)",
                color: "#ff7351",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,115,81,0.20)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(255,115,81,0.10)"}
            >
              🚫 Bloquear Contacto
            </button>
          )}
        </div>
      </aside>
    </>
  );
}
